/* Talking to the portal's own API.

   One helper, so that credentials, the content type and error handling are not
   decided again in every component. Errors carry the server's Czech sentence:
   the API says what a person can do next, and swallowing that in favour of
   "something went wrong" throws away the only useful part.

   OWNED BY ITEM 6 OF docs/plan/00-prestavba-2026-08.md. */

/* Me lives in types.ts with the rest of the contract; re-exported here so a
   component talking to the API imports from one place. */
export type { Me } from "./types";
export { can } from "./types";

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: "same-origin",
    /* The content type only where there is content: Fastify answers a bodiless
       DELETE that claims JSON with a 400 about an empty body, which surfaced
       as a delete button that did nothing. */
    headers: {
      ...(init?.body != null ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.detail ?? `HTTP ${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/* One row of the material, as the administration lists it. The material itself
   is still fetched through lib/content.ts like any reader's — this is only what
   a tree of it needs, plus what a save has to know it is replacing. */
export interface ContentRow {
  path: string;
  kind: string;
  status: string;
  sha256: string;
  size: number;
  updated_at: string;
  updated_by: string | null;
  meta?: {
    title: string;
    type: string;
    subject?: string;
    strand?: string[];
    year?: number;
  };
}

export interface ContentVersion {
  id: string;
  sha256: string;
  size: number;
  saved_at: string;
  saved_by: string | null;
}

/** One edit of one document: where it landed, and how heavy what it replaced
 *  was. The history at the level a correction happens. */
export interface ContentEdit {
  id: string;
  at: string;
  size: number;
  saved_at: string;
  saved_by: string | null;
}

/** What a translation would cost before anyone pays for it, and whether the
 *  service can perform one at all. */
export interface Translatable {
  fields: number; chars: number; costUsd: number;
  ready: boolean; spentToday: number; dailyChars: number;
}

/** What one did: fields filled, and the addresses it refused to store because
 *  the translation came back with the markdown broken. */
export interface TranslateRun {
  fields: number; chars: number; costUsd: number; refused: string[];
}

/** What reading a script aloud would cost, and what is recorded already. */
export interface Voiceable {
  chars: number; costUsd: number; ready: boolean;
  spentToday: number; dailyChars: number;
  recorded: { slug: string; size: number } | null;
  voice: string; model: string;
}

/** What one recording came to. */
export interface VoiceRun {
  slug: string; bytes: number; chars: number; costUsd: number; seconds: number;
}

/** An English half a machine wrote and nobody has read yet. */
export interface Mark {
  owner: string; ownerId: string; path: string; from: string;
  /** Where it sits in the document: `kit[1].claim`. */
  at: string;
}

export interface UsageRun {
  username: string; at: string; status: string;
  output_tokens: number; cost_usd: number; seconds: number | null;
}

export interface Usage {
  today_runs: number; today_cost_usd: number;
  month_runs: number; month_cost_usd: number;
  live: number;
  limits: { daily_max: number; user_daily_max: number; daily_cost_usd: number; concurrent_max: number };
  effective: { model: string; effort: string; fast: boolean; key: boolean };
  recent: UsageRun[];
}

export interface UserRow {
  id: string; username: string; role: string; disabled: boolean;
  last_login_at: string | null; runs_today: number;
}

export interface Minted { username: string; password: string }

export interface MediaRow {
  slug: string; title: string; content_type: string;
  size: number; sha256: string; updated_at: string; updated_by: string | null;
}
